import { useLayoutEffect, useRef } from "react";

/**
 * Two toast systems share the login page, and a failed OTP verify raises one
 * of each in the same tick:
 *
 *   ToastHost (App.js)     .toast-host     fixed, top 24px, right 24px
 *   Login's own            .toast-message  fixed, top 24px, centred
 *
 * On desktop they sit side by side. At phone width the two boxes overlap and
 * the one underneath cannot be read. So on a phone the login toast moves down
 * by however much room ToastHost's stack takes (MOBILE.loginToastStack).
 *
 * Neither component knows about the other: they are in separate trees, and
 * the stack's height changes as toasts come and go and as long messages wrap.
 * So ToastHost publishes that height on <html>, and the login toast reads it.
 * The variable exists only while the stack is on screen, so with no ToastHost
 * toast the login toast sits at 24px exactly as before.
 */

/** The stack's height plus the 10px gap the stack uses between its own toasts. */
export const TOASTS_H = "--zym-toasts-h";
const GAP_PX = 10;

/**
 * ToastHost's side. Attach the ref to `.toast-host`.
 *
 * A layout effect, not a plain one: a failed verify sets both toasts from the
 * same callback, and the variable has to be in place before that frame paints
 * or the login toast shows for one frame on top of the other.
 *
 * @param {boolean} on     the mobile query && the flag
 * @param {boolean} shown  ToastHost has at least one toast (it renders null otherwise)
 */
export const useToastStackHeight = (on, shown) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!on || !shown || !el) return undefined;
    const root = el.ownerDocument.documentElement;

    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      root.style.setProperty(TOASTS_H, `${h + GAP_PX}px`);
    };
    publish();

    /* Same feature test as useCssVarHeight. Without the observer the first
       measurement still holds; only a toast added or dismissed while the
       login toast is up goes unseen. */
    const ro =
      typeof window.ResizeObserver === "function"
        ? new window.ResizeObserver(publish)
        : null;
    if (ro) ro.observe(el);

    return () => {
      if (ro) ro.disconnect();
      root.style.removeProperty(TOASTS_H);
    };
  }, [on, shown]);

  return ref;
};

/**
 * The login toast's side: its desktop `top` (login.scss, 24px) plus the stack.
 * The transition carries it up when a ToastHost toast is dismissed while it
 * is still showing.
 */
export const LOGIN_TOAST_BELOW_STACK = {
  top: `calc(24px + var(${TOASTS_H}, 0px))`,
  transition: "top 160ms ease",
};
